import {fetchJson, getInputValue} from './dom';

type CrmDiffDirection = 'toCRM' | 'fromCRM';

type AddPayorResponse = {
    result?: string;
    message?: string;
    response?: { code?: number };
};

type CrmDiffConfig = {
    fieldLabel: string;
    successLabel: string;
    dwFieldId: string;
    crmFieldId: string;
    buildPayload: (direction: CrmDiffDirection, value: string) => Record<string, string>;
    emptyDwRemovesInCrm?: boolean;
    emptyCrmRemovesInDw?: boolean;
};

function getBasePayorData(): Record<string, string> {
    return {
        external_payor: getInputValue('external_payor'),
        payor_type: getInputValue('payor_type'),
        org_id: getInputValue('org_id'),
    };
}

function getConfirmText(
    config: CrmDiffConfig,
    direction: CrmDiffDirection,
    dwValue: string,
    crmValue: string,
): string {
    const target = direction === 'toCRM' ? 'CRM' : 'Donor Wallet';
    const source = direction === 'toCRM' ? 'Donor Wallet' : 'CRM';

    if (config.emptyDwRemovesInCrm && direction === 'toCRM' && dwValue === '') {
        return `This will remove ${config.fieldLabel} in CRM too.`;
    }
    if (config.emptyCrmRemovesInDw && direction === 'fromCRM' && crmValue === '') {
        return `This will remove ${config.fieldLabel} in PaymentNGN too.`;
    }

    return `This will update ${config.fieldLabel} in ${target} from ${source}`;
}

function confirmCrmUpdate(config: CrmDiffConfig, direction: CrmDiffDirection): void {
    const dwValue = getInputValue(config.dwFieldId);
    const crmValue = getInputValue(config.crmFieldId);
    const target = direction === 'toCRM' ? 'CRM' : 'Donor Wallet';
    const value = direction === 'toCRM' ? dwValue : crmValue;

    swal(
        {
            title: `Do you want to update ${config.fieldLabel} in ${target}`,
            text: getConfirmText(config, direction, dwValue, crmValue),
            type: 'info',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            closeOnConfirm: false,
            showLoaderOnConfirm: true,
            closeOnCancel: true,
        },
        (isConfirm: boolean) => {
            if (!isConfirm) {
                swal('Cancelled', 'Callback was not made.', 'error');
                return;
            }

            swal(`Updating ${config.successLabel} In ${target}`, 'Please Wait ...');

            const data = {
                ...getBasePayorData(),
                ...config.buildPayload(direction, value),
                ...(direction === 'toCRM' ? {update_crm: 'true'} : {update_dw: 'true'}),
            };

            void fetchJson<AddPayorResponse>('/ajax/addpayor', data).then((response) => {
                if (response.result === 'error') {
                    swal(`Error Updating ${config.successLabel}`, response.message ?? '', 'error');
                    return;
                }
                if (response.response?.code === 0) {
                    swal('A server error has occurred', 'Please contact an administrator.', 'error');
                    return;
                }
                swal('Success', `${config.successLabel} Updated Successfully`, 'success');
                window.location.reload();
            });
        },
    );
}

const CRM_DIFF_CONFIGS: Record<string, CrmDiffConfig> = {
    email: {
        fieldLabel: 'email',
        successLabel: 'Email',
        dwFieldId: 'dw_email',
        crmFieldId: 'crm_email',
        emptyDwRemovesInCrm: true,
        emptyCrmRemovesInDw: true,
        buildPayload: (_direction, value) => ({email: value}),
    },
    phone: {
        fieldLabel: 'phone',
        successLabel: 'Phone',
        dwFieldId: 'dw_phone',
        crmFieldId: 'crm_phone',
        emptyDwRemovesInCrm: true,
        emptyCrmRemovesInDw: true,
        buildPayload: (_direction, value) => ({mobile: value}),
    },
    address: {
        fieldLabel: 'address',
        successLabel: 'Address',
        dwFieldId: 'dw_address1',
        crmFieldId: 'crm_address1',
        buildPayload: (direction) => {
            const prefix = direction === 'toCRM' ? 'dw' : 'crm';
            return {
                postcode: getInputValue(`${prefix}_postcode`),
                street_address: getInputValue(`${prefix}_address1`),
                street_address2: getInputValue(`${prefix}_address2`),
                suburb: getInputValue(`${prefix}_suburb`),
                state: getInputValue(`${prefix}_state`),
                country: getInputValue(`${prefix}_country`),
            };
        },
    },
};

export function setupCrmDiff(): void {
    if (!document.querySelector('[data-crm-diff]')) {
        return;
    }

    document.querySelectorAll<HTMLElement>('[data-crm-diff]').forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
            event.preventDefault();
            const direction = trigger.dataset.crmDiff as CrmDiffDirection | undefined;
            const field = trigger.dataset.crmField;
            if (!direction || !field) {
                return;
            }
            const config = CRM_DIFF_CONFIGS[field];
            if (!config) {
                return;
            }
            confirmCrmUpdate(config, direction);
        });
    });
}
